import { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import _css from "@styled-system/css";

import Popover from "../MenuPopover/MenuPopover";
import { useHover } from "../useHover.hook";
import Link from "../Link/Link";
import { MenuLink } from "../Navigation/MenuLink.type";
import { processEventLabel } from "../Navigation/navItem.functions";

import {
  NavPopoverProps,
  NavPopoverWithSectionsProps,
  NavPopoverWithChildrenProps,
} from "../BCAHeader/bcaHeader.type";

const TopLevelLink = styled.li`
  position: relative;
  margin-right: 20px;
  display: flex;
  align-items: center;
  height: 76px;

  @media only screen and (min-width: 950px) {
    margin-right: 40px;
  }

  &:last-of-type {
    margin-right: 0;
  }
  &:hover ul {
    visibility: visible;
  }
  &:focus-within ul {
    visibility: visible;
  }
  &:hover div {
    visibility: visible;
  }
  &:focus-within div {
    visibility: visible;
  }
  &:last-of-type {
    margin-right: 0;
  }
`;

const NavPopover = ({ children, as, css, role }: NavPopoverProps) => {
  const themeContext = useContext(ThemeContext);
  const itemsFontSize = themeContext.fontSizes["body"];

  return (
    <Popover
      height="auto"
      width="auto"
      as={as}
      role={role}
      css={`
        position: absolute;
        top: calc(100% - 15px);
        left: 50%;
        transform: translateX(-50%);
        visibility: hidden;
        padding: 24px 28px;
        white-space: nowrap;
        font-size: ${itemsFontSize}px;
        ${css}
      `}
    >
      {children}
    </Popover>
  );
};

const A2 = styled(Link)`
  display: block;
  ${_css({
    "&:first-child": {
      color: "grey-blue",
      textTransform: "uppercase",
      fontSize: "label",
      fontWeight: "semiBold",
      paddingBottom: 1,
      textDecoration: "none",
      cursor: "auto",
    },
  })};
`;

const Ul = styled.ul`
  display: inline-flex;
  flex-direction: column;
  padding: 0 30px;
  position: relative;
  min-height: 163px;
  &:after {
    content: "";
    position: absolute;
    right: 0;
    width: 1px;
    height: 150px;
    background: #dedede;
    top: 6px;
  }
  &:first-of-type {
    padding-left: 0;
  }
  &:last-of-type {
    padding-right: 0;
    &:after {
      content: none;
    }
  }
`;

const PopoverWithSections = ({
  sections,
  eventAction,
  eventSection,
}: NavPopoverWithSectionsProps) => {
  return (
    <NavPopover
      as="div"
      role="submenu"
      css={`
        padding-top: 30px;
      `}
      aria-haspopup="true"
    >
      {sections.map(({ children, id }) => (
        <Ul key={id} role="menu" aria-haspopup="true">
          {children
            .filter((c) => c.isVisible)
            .map((link) => {
              const eventLabel = processEventLabel(link.label);

              return link.renderAsText ? (
                <A2 key={link.id} role="menuitem" as="span">
                  {link.label}
                </A2>
              ) : (
                <A2
                  key={link.id}
                  href={link.link}
                  role="menuitem"
                  target={link.shouldOpenInNewWindow ? "_blank" : "_top"}
                  data-en="click"
                  data-ec="button"
                  data-el={eventLabel}
                  data-ea={eventAction}
                  data-es={eventSection}
                >
                  {link.label}
                </A2>
              );
            })}
        </Ul>
      ))}
    </NavPopover>
  );
};

const PopoverWithChildren = ({
  children,
  eventAction,
  eventSection,
}: NavPopoverWithChildrenProps) => {
  return (
    <NavPopover
      as="ul"
      css={`
        display: flex;
        flex-direction: column;
      `}
      role="submenu"
    >
      {children
        .filter((c) => c.isVisible)
        .map((link) => {
          const eventLabel = processEventLabel(link.label);

          return (
            <Link
              key={link.id}
              href={link.link}
              role="menuitem"
              target={link.shouldOpenInNewWindow ? "_blank" : "_top"}
              data-en="click"
              data-ec="button"
              data-el={eventLabel}
              data-ea={eventAction}
              data-es={eventSection}
            >
              {link.label}
            </Link>
          );
        })}
    </NavPopover>
  );
};

const NavItem = ({ item }: { item: MenuLink }) => {
  const [hoverRef] = useHover<HTMLAnchorElement>();
  const { children, sections } = item;
  const eventLabel = processEventLabel(item.label);
  const eventAction = item.label === "Search" ? "search" : "navigate";

  const getInner = () => {
    if (children) {
      return (
        <PopoverWithChildren
          children={children}
          eventAction={eventAction}
          eventSection={eventLabel}
        />
      );
    }
    if (sections) {
      return (
        <PopoverWithSections
          sections={sections}
          eventAction={eventAction}
          eventSection={eventLabel}
        />
      );
    }
    return null;
  };

  return (
    <TopLevelLink>
      <Link
        href={item.link}
        ref={hoverRef}
        data-el={eventLabel}
        data-ea={eventAction}
        data-es="header"
        target={item.shouldOpenInNewWindow ? "_blank" : "_top"}
      >
        {item.label}
      </Link>
      {getInner()}
    </TopLevelLink>
  );
};

export default NavItem;
